import api from "../components/login/interceptors";
import { handleLogout } from "../components/auth/authHelpers";

// Client for the AI Interviewer service: a separate backend from SiftHire that reuses the same JWT
// (added by the `api` interceptor). `interviewId` is always the SiftHire interview `_id`.
// Types follow docs/frontend_integration.md in the ai-interviewer repo.

/**
 * @typedef {"SCHEDULED"|"PREPARING"|"READY"|"BOT_JOINING"|"WAITING_FOR_CANDIDATE"|"IN_PROGRESS"|"PAUSED"
 *   |"EVALUATING"|"COMPLETED"|"CANDIDATE_NO_SHOW"|"CANDIDATE_DECLINED"|"FAILED"|"CANCELLED"} AISessionStatus
 * @typedef {"ai_interviewer"|"panelist_with_ai"} AIMode
 * @typedef {"STRONG_HIRE"|"HIRE"|"PROCEED"|"BORDERLINE"|"DO_NOT_PROCEED"|"INSUFFICIENT_EVIDENCE"} Recommendation
 *
 * @typedef {{ code: string, category: "integrity"|"technical", severity: "info"|"warning"|"critical",
 *   message: string, data: Object<string, *>, at: string, count: number }} Warning
 *
 * @typedef {{ candidate_display_name?: string|null, candidate_name_matched?: boolean|null, camera_checked?: boolean,
 *   camera_off_seconds?: number, camera_off_events?: number, unknown_participants?: string[],
 *   max_participants?: number }} Integrity
 *
 * @typedef {{
 *   interview_id: string, session_id: string, status: AISessionStatus,
 *   phase: "DISCLOSURE"|"SECTION"|"CANDIDATE_QUESTIONS"|"ENDED"|null,
 *   candidate_id: string|null, jd_id: string|null,
 *   config: { mode: AIMode, round: { id: string|null, name: string|null }, duration_minutes: number,
 *     language: string, seniority: string|null, focus_skills: string[], company_facts: string|null,
 *     store_audio: boolean },
 *   plan_version: number|null, plan_status: "ready"|"pending",
 *   progress: null|{ sections: { name: string, status: string, budget_minutes: number, used_minutes: number|null }[],
 *     questions_asked?: number, follow_ups?: number },
 *   consent: null|{ disclosed_at: string, accepted: boolean|string, response_text: string },
 *   completion_reason: string|null,
 *   failure: null|{ code: string, message?: string, retryable: boolean },
 *   report_id: string|null, warnings: Warning[], integrity: Integrity,
 *   timings: { scheduled_start?: string, scheduled_end?: string, bot_joined_at?: string, started_at?: string,
 *     ended_at?: string, duration_seconds?: number },
 *   created_at: string, updated_at: string
 * }} AISession
 *
 * @typedef {{ mode?: AIMode, duration_minutes?: number,
 *   seniority?: "junior"|"mid"|"mid_senior"|"senior"|"staff_plus", focus_skills?: string[], language?: string,
 *   company_facts?: string }} EnableOptions
 *
 * @typedef {{ expected_points: string[], important_points: string[],
 *   level_signals: { basic: string, intermediate: string, advanced: string }, red_flags: string[] }} PlanRubric
 * @typedef {{ question_id: string, text: string, category: string, skill: string|null,
 *   difficulty: "basic"|"intermediate"|"advanced"|"expert", priority: string, is_spare: boolean,
 *   claim_id: string|null, est_minutes: number, rubric: PlanRubric, follow_up_probes: string[],
 *   easier_variant: string|null, harder_variant: string|null }} PlanQuestion
 * @typedef {{ section_id: string, name: string, duration_minutes: number, question_count: number,
 *   questions: PlanQuestion[] }} PlanSection
 * @typedef {{ sections: PlanSection[], role_summary: *, skills_to_assess: *, resume_claims: *, version: number,
 *   plan_warnings: string[], source: "generated"|"edited", mode: AIMode,
 *   round: { name: string, focus: string, weights: Object<string, number>,
 *     source: "rules"|"cache"|"llm"|"default" } }} Plan
 *
 * @typedef {{ text: string, evidence_question_ids: string[] }} Evidence
 * @typedef {{ by: string, text: string, question_id: string|null, at: string }} PanelNote
 * @typedef {{ reviewer: string, rating: number|null, notes: string|null, at: string }} PanelistReview
 * @typedef {{ status: "pending"|"reviewed", decision: "advance"|"reject"|"hold"|"another_round"|null,
 *   agrees_with_ai: boolean|null, notes: string|null, reviewer_user_id: string|null,
 *   reviewed_at: string|null }} Review
 * @typedef {{
 *   report_id: string, version: number, status: string, created_at: string,
 *   interview_id: string, session_id: string, candidate_id: string, jd_id: string,
 *   recommendation: Recommendation, hiring_recommendation: string, overall_score: number,
 *   job_match_score: number|null, technical_score: number|null, problem_solving_score: number|null,
 *   practical_experience_score: number|null, communication_of_technical_ideas_score: number|null,
 *   section_scores: Object<string, number>,
 *   skills: Object<string, { score: number|null, demonstrated_level: string|null, questions: number,
 *     jd_priority: "must_have"|"important"|"nice_to_have" }>,
 *   interview_summary: string, strengths: Evidence[], weaknesses: Evidence[],
 *   resume_validation: { claim_id: string, claim: string,
 *     status: "STRONGLY_SUPPORTED"|"SUPPORTED"|"PARTIALLY_SUPPORTED"|"NOT_VALIDATED"|"CONTRADICTED",
 *     reason: string, evidence_question_ids: string[] }[],
 *   untested_areas: string[], suggested_focus_next_round: string[], reviewer_attention_points: string[],
 *   candidate_questions: string[], coverage: number, scored_answers: number, insufficient_evidence: boolean,
 *   data_quality_notes: string[],
 *   interview_meta: { started_at: string, ended_at: string, duration_seconds: number, completion_reason: string,
 *     questions_asked: number, follow_ups: number },
 *   integrity: Integrity & { warnings: Warning[] }, technical_issues: Warning[],
 *   panelist?: { average_rating: number|null, questions_rated: number, ai_agreement: number|null,
 *     disagreements: string[],
 *     questions: { question_id: string, question: string, ai_score: number|null, panelist_rating: number|null,
 *       disagreement?: boolean, reviews: PanelistReview[] }[],
 *     notes: PanelNote[], disclosure: { disclosed_by: string, at: string }|null },
 *   disclaimer: string, review: Review
 * }} Report
 *
 * @typedef {{
 *   question_id: string, seq: number, section_id: string, source: string, parent_question_id: string|null,
 *   thread_root_question_id: string, follow_ups: string[], category: string, skill: string|null,
 *   difficulty: "basic"|"intermediate"|"advanced"|"expert", claim_id: string|null, question: string,
 *   spoken_text: string, candidate_answer: string|null, asked_at: string, answered_at: string|null,
 *   response_duration_seconds: number|null,
 *   outcome: "answered"|"partial"|"dont_know"|"skipped"|"no_response"|null,
 *   eval_status: "pending"|"running"|"done"|"failed"|"not_applicable",
 *   evaluation: null|{ score: number|null, dimensions: Object<string, number>, covered_points: string[],
 *     missing_points: string[], incorrect_statements: string[], strengths: string[],
 *     demonstrated_level: string|null, evidence_sufficiency: string, evaluator_notes: string },
 *   flags: null|{ possible_transcription_issue: boolean, multiple_speakers: boolean, cut_off_by_time: boolean,
 *     injection_attempt: boolean }
 * }} Question
 *
 * @typedef {"probe_deeper"|"clarify"|"verify_claim"|"easier"|"harder"|"missing_point"} FollowUpPurpose
 * @typedef {"next"|"asked"|"skipped"} BankMarkStatus
 * @typedef {{
 *   status: AISessionStatus, mode: AIMode,
 *   current: { question_id: string, question: string, asked_by: string|null, answer: string,
 *     status: "asking"|"answering"|"analysed" }|null,
 *   suggestions: { for_question_id: string, for_question: string, matched_question_id: string|null,
 *     answer_summary: string, covered_points: string[], missing_points: string[],
 *     follow_ups: { text: string, purpose: FollowUpPurpose, why: string }[],
 *     next_question: { question_id: string|null, text: string, why: string }|null }|null,
 *   coverage: { skills: { skill: string, priority: string, covered: boolean }[],
 *     claims: { claim_id: string, text: string, importance: string, covered: boolean }[] },
 *   asked_bank_ids: string[], exchanges: number,
 *   bank_marks: Object<string, { status: BankMarkStatus, by: string, at: string }>,
 *   notes: PanelNote[], disclosure: { disclosed_by: string, at: string }|null, updated_at: string|null
 * }} LiveView
 *
 * @typedef {{ segments: { seq: number, speaker: "interviewer"|"candidate"|"panelist"|"other"|"unknown",
 *   speaker_name?: string|null, text: string, question_id: string|null, intent: string|null,
 *   created_at: string }[], next_after_seq: number, complete: boolean }} TranscriptPage
 */

export const AI_INTERVIEWER_API_URL = (
  process.env.REACT_APP_AI_INTERVIEWER_API_URL || "https://ai-interviewer-api.sifthire.com/api"
).replace(/\/+$/, "");

const aiUrl = (path) => `${AI_INTERVIEWER_API_URL}${path}`;
const sessionPath = (interviewId, suffix = "") => `/ai-interviews/${encodeURIComponent(interviewId)}${suffix}`;

export const AI_STATUS = {
  SCHEDULED: "SCHEDULED",
  PREPARING: "PREPARING",
  READY: "READY",
  BOT_JOINING: "BOT_JOINING",
  WAITING_FOR_CANDIDATE: "WAITING_FOR_CANDIDATE",
  IN_PROGRESS: "IN_PROGRESS",
  PAUSED: "PAUSED",
  EVALUATING: "EVALUATING",
  COMPLETED: "COMPLETED",
  CANDIDATE_NO_SHOW: "CANDIDATE_NO_SHOW",
  CANDIDATE_DECLINED: "CANDIDATE_DECLINED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
};

export const AI_STATUS_LABELS = {
  SCHEDULED: "AI scheduled",
  PREPARING: "Preparing questions",
  READY: "AI ready",
  BOT_JOINING: "AI joining the meeting",
  WAITING_FOR_CANDIDATE: "Waiting for candidate",
  IN_PROGRESS: "Interview in progress",
  PAUSED: "Paused",
  EVALUATING: "Scoring",
  COMPLETED: "Completed",
  CANDIDATE_NO_SHOW: "Candidate didn't join",
  CANDIDATE_DECLINED: "Candidate declined the AI interview",
  FAILED: "Failed",
  CANCELLED: "AI off",
};

export const AI_MODE = {
  AI_INTERVIEWER: "ai_interviewer",
  PANELIST_WITH_AI: "panelist_with_ai",
};

export const AI_MODE_LABELS = {
  ai_interviewer: "AI interviewer",
  panelist_with_ai: "Human + AI assistant",
};

export const COMPLETION_REASON_LABELS = {
  finished: "Finished",
  time_up: "Time ran out",
  candidate_requested_end: "Candidate ended the interview",
  candidate_left: "Candidate left",
  camera_off: "Camera was off too long",
  no_response: "Nothing was heard",
  camera_not_enabled: "Camera never came on",
  meeting_ended: "Meeting ended",
  everyone_left: "Everyone left",
  time_limit: "Time limit reached",
};

const FINAL_STATUSES = ["COMPLETED", "CANDIDATE_NO_SHOW", "CANDIDATE_DECLINED", "FAILED", "CANCELLED"];
// AI can only be turned off before the bot joins
const DISABLEABLE_STATUSES = ["SCHEDULED", "PREPARING", "READY"];
// Questions and transcript exist once the interview has started
const STARTED_STATUSES = ["IN_PROGRESS", "PAUSED", "EVALUATING", "COMPLETED"];
// The live dashboard polls /live every few seconds in these statuses
const LIVE_STATUSES = ["BOT_JOINING", "WAITING_FOR_CANDIDATE", "IN_PROGRESS"];
// The plan can be regenerated from these statuses (otherwise PLAN_LOCKED)
const PLAN_GENERATABLE_STATUSES = ["SCHEDULED", "READY", "FAILED"];

export const isFinalAIStatus = (status) => FINAL_STATUSES.includes(status);
export const canDisableAI = (status) => DISABLEABLE_STATUSES.includes(status);
export const hasAIInterviewStarted = (status) => STARTED_STATUSES.includes(status);
export const isLiveAIStatus = (status) => LIVE_STATUSES.includes(status);
export const canGenerateAIPlan = (status) => PLAN_GENERATABLE_STATUSES.includes(status);
export const canEditAIPlan = (status) => status === AI_STATUS.READY;

const POLL_WINDOW_MS = 60 * 60 * 1000;

// Poll while the session isn't final and the interview is within ±1 hour of its start
export const shouldPollAISession = (status, startTime, now = Date.now()) => {
  if (!status || isFinalAIStatus(status)) return false;
  const start = new Date(startTime).getTime();
  if (Number.isNaN(start)) return false;
  return Math.abs(now - start) <= POLL_WINDOW_MS;
};

// Summary kept on the SiftHire interview document, or a full AISession. Returns { enabled, status, ... } or null.
export const aiSummaryOf = (interview, session) => {
  const summary = interview?.ai_interview || interview?.aiInterview;
  if (session) {
    return {
      enabled: session.status !== AI_STATUS.CANCELLED,
      status: session.status,
      mode: session.config?.mode || interview?.ai_mode || interview?.aiMode || null,
      roundName: session.config?.round?.name || "",
      warnings: Array.isArray(session.warnings) ? session.warnings : [],
      overallScore: session.overall_score ?? summary?.overall_score,
      recommendation: session.recommendation ?? summary?.recommendation,
      failureMessage: session.failure?.message || "",
    };
  }
  if (!summary) return null;
  if (summary.enabled === false) return { enabled: false, status: AI_STATUS.CANCELLED, warnings: [] };
  return {
    enabled: true,
    status: summary.status,
    mode: interview?.ai_mode || interview?.aiMode || null,
    roundName: "",
    warnings: [],
    overallScore: summary.overall_score,
    recommendation: summary.recommendation,
    failureMessage: "",
  };
};

// Errors

// Copy from the guide's error tables, used where the guide says what to show for a code
const ERROR_COPY = {
  VALIDATION_ERROR: "Some AI interviewer settings are invalid.",
  INTERVIEW_NOT_FOUND: "Interview not found.",
  CANDIDATE_NOT_FOUND: "Candidate not found.",
  INVALID_STATUS_TRANSITION: "Only scheduled interviews can use the AI interviewer.",
  AI_INTERVIEW_ALREADY_ENABLED: "AI is already enabled for this interview.",
  MEET_LINK_MISSING: "The interview needs a Google Meet link.",
  MEETING_PROVIDER_UNSUPPORTED: "AI interviewer is available for Google Meet only.",
  RESUME_NOT_AVAILABLE: "The candidate's resume hasn't been parsed.",
  DURATION_EXCEEDS_SLOT: "The AI interview is longer than the scheduled slot.",
  AI_INTERVIEW_NOT_FOUND: "AI isn't enabled for this interview.",
  INVALID_SESSION_TRANSITION: "Too late: the bot has already joined or the interview has started.",
};

export const getAIErrorCode = (error) => error?.response?.data?.code;

// The JWT library answers { msg } with 401, or 422 for a bad or expired token: treat both as "sign in again"
export const isAIAuthError = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status === 401) return true;
  return status === 422 && Boolean(data?.msg) && !data?.code;
};

// Shows the service's `error`; branch on getAIErrorCode() for logic
export const getAIErrorMessage = (error, fallback = "Something went wrong with the AI interviewer.") => {
  if (isAIAuthError(error)) return "Your session has expired. Please sign in again.";
  const data = error?.response?.data;
  if (data?.error) return data.error;
  if (data?.code && ERROR_COPY[data.code]) return ERROR_COPY[data.code];
  return error?.response ? fallback : error?.message || fallback;
};

// Message for a failed enable/disable, worded per `code` as in the guide
export const describeAIChangeError = (error, fallback) => {
  const code = getAIErrorCode(error);
  const details = error?.response?.data?.details || {};
  if (code === "DURATION_EXCEEDS_SLOT" && details.slot_minutes) {
    return `${ERROR_COPY.DURATION_EXCEEDS_SLOT} The slot is ${details.slot_minutes} minutes.`;
  }
  if (code === "VALIDATION_ERROR" && details.field) return `Invalid field: ${details.field}.`;
  if (code && ERROR_COPY[code] && !isAIAuthError(error)) return ERROR_COPY[code];
  return getAIErrorMessage(error, fallback);
};

// `details.errors` from 422 PLAN_INVALID, as text
export const planValidationErrors = (error) => {
  const errors = error?.response?.data?.details?.errors;
  if (!Array.isArray(errors)) return [];
  return errors.map((item) => (typeof item === "string" ? item : item?.message || item?.error || JSON.stringify(item)));
};

// Every call goes through here so an expired session logs out like the rest of SiftHire
const request = async (method, path, { data, params } = {}) => {
  try {
    const response = await api.request({ method, url: aiUrl(path), data, params });
    return response.data;
  } catch (error) {
    if (isAIAuthError(error)) handleLogout();
    throw error;
  }
};

// Resolves to `null` instead of throwing when the error has `code`
const nullOn = (code) => (error) => {
  if (getAIErrorCode(error) === code) return null;
  throw error;
};

// Sessions

/** @param {string} interviewId @param {EnableOptions} [options] @returns {Promise<AISession>} */
export const enableAIInterview = (interviewId, options = {}) =>
  request("post", sessionPath(interviewId, "/enable"), { data: options });

/** @returns {Promise<AISession>} the session, now CANCELLED */
export const disableAIInterview = (interviewId) => request("post", sessionPath(interviewId, "/disable"));

/** @returns {Promise<AISession|null>} null when AI isn't enabled for the interview */
export const fetchAISession = (interviewId) =>
  request("get", sessionPath(interviewId)).catch(nullOn("AI_INTERVIEW_NOT_FOUND"));

/**
 * @param {{ status?: AISessionStatus, jd_id?: string, candidate_id?: string, page?: number, limit?: number }} [filters]
 * @returns {Promise<{ sessions: AISession[], count: number, page: number, limit: number }>}
 */
export const listAISessions = (filters = {}) => request("get", "/ai-interviews", { params: filters });

const SESSION_PAGE_LIMIT = 100;
const SESSION_MAX_PAGES = 10;

/** @returns {Promise<Object<string, AISession>>} SiftHire interview id → AISession */
export const fetchAISessionsByInterview = async (filters = {}) => {
  const sessions = {};
  for (let page = 1; page <= SESSION_MAX_PAGES; page += 1) {
    const data = await listAISessions({ ...filters, page, limit: SESSION_PAGE_LIMIT });
    const items = data?.sessions || [];
    items.forEach((session) => {
      if (session.interview_id) sessions[session.interview_id] = session;
    });
    if (items.length < SESSION_PAGE_LIMIT || page * SESSION_PAGE_LIMIT >= (data?.count ?? 0)) break;
  }
  return sessions;
};

// Turns AI on or off for each item: [{ interviewId, label, action: "enable" | "disable", mode }].
// Returns the items that failed, each with `code` and `message`.
export const applyAIChanges = async (items) => {
  const results = await Promise.all(
    items.map(async (item) => {
      try {
        if (item.action === "disable") await disableAIInterview(item.interviewId);
        else await enableAIInterview(item.interviewId, item.mode ? { mode: item.mode } : {});
        return null;
      } catch (error) {
        const code = getAIErrorCode(error);
        // Already in the requested state
        if (item.action !== "disable" && code === "AI_INTERVIEW_ALREADY_ENABLED") return null;
        if (item.action === "disable" && code === "AI_INTERVIEW_NOT_FOUND") return null;
        const fallback = item.action === "disable" ? "AI couldn't be turned off." : "AI couldn't be enabled.";
        return { ...item, code: code || "", message: describeAIChangeError(error, fallback) };
      }
    })
  );
  return results.filter(Boolean);
};

// Question plan

/** @returns {Promise<Plan|null>} null until the plan is generated */
export const fetchAIPlan = (interviewId) =>
  request("get", sessionPath(interviewId, "/plan")).catch(nullOn("PLAN_NOT_FOUND"));

/** Generates the questions now. Errors: 409 PLAN_LOCKED, 502 LLM_PROVIDER_ERROR. @returns {Promise<Plan>} */
export const generateAIPlan = (interviewId) => request("post", sessionPath(interviewId, "/plan"));

/** Saves the full plan; only while READY. Errors: 409 PLAN_LOCKED, 422 PLAN_INVALID. @returns {Promise<Plan>} */
export const saveAIPlan = (interviewId, plan) => request("put", sessionPath(interviewId, "/plan"), { data: plan });

// Live dashboard (panelist_with_ai)

/** @returns {Promise<LiveView>} */
export const fetchAILive = (interviewId) => request("get", sessionPath(interviewId, "/live"));

/** @returns {Promise<{ disclosed_by: string, at: string }>} */
export const confirmAIDisclosure = (interviewId) => request("post", sessionPath(interviewId, "/disclosed"));

/**
 * @param {BankMarkStatus} status
 * @returns {Promise<{ question_id: string, status: BankMarkStatus, by: string, at: string }>}
 */
export const markAIBankQuestion = (interviewId, bankQuestionId, status) =>
  request("post", sessionPath(interviewId, `/bank/${encodeURIComponent(bankQuestionId)}`), { data: { status } });

/**
 * Rates an exchange (`P1`, `P2`...). Rating, notes or both; sending again replaces this panelist's rating.
 * @param {{ rating?: number, notes?: string }} body
 * @returns {Promise<PanelistReview[]>} the question's panelist_reviews
 */
export const rateAIAnswer = (interviewId, questionId, { rating, notes } = {}) => {
  const body = {};
  if (typeof rating === "number") body.rating = rating;
  if (notes && notes.trim()) body.notes = notes.trim();
  return request("post", sessionPath(interviewId, `/questions/${encodeURIComponent(questionId)}/rating`), { data: body });
};

/** @param {{ text: string, questionId?: string }} note @returns {Promise<PanelNote>} */
export const addAINote = (interviewId, { text, questionId } = {}) => {
  const body = { text: (text || "").trim() };
  if (questionId) body.question_id = questionId;
  return request("post", sessionPath(interviewId, "/notes"), { data: body });
};

// Results

/** @returns {Promise<Report|null>} null until scoring finishes (REPORT_NOT_READY) */
export const fetchAIReport = (interviewId) =>
  request("get", sessionPath(interviewId, "/report")).catch(nullOn("REPORT_NOT_READY"));

/** @returns {Promise<Question[]>} */
export const fetchAIQuestions = async (interviewId) => {
  const data = await request("get", sessionPath(interviewId, "/questions"));
  return data?.questions || [];
};

/** @returns {Promise<TranscriptPage>} */
export const fetchAITranscript = (interviewId, afterSeq = 0) =>
  request("get", sessionPath(interviewId, "/transcript"), { params: { after_seq: afterSeq } });

/**
 * @param {{ decision: "advance"|"reject"|"hold"|"another_round", agreesWithAi?: boolean, notes?: string }} review
 * @returns {Promise<Review>}
 */
export const submitAIReview = (interviewId, { decision, agreesWithAi, notes }) => {
  const body = { decision };
  if (typeof agreesWithAi === "boolean") body.agrees_with_ai = agreesWithAi;
  if (notes && notes.trim()) body.notes = notes.trim();
  return request("post", sessionPath(interviewId, "/review"), { data: body });
};
